/**
 * Componente de Card para exibição de Template WhatsApp
 * Sprint 2 - Notificame Integration
 */

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import {
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  FileText,
  MessageSquare,
  Globe,
  ShieldCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import VisibilityBadge from '@/components/shared/VisibilityBadge';
import type { EntityVisibility } from '@/hooks/useEntityVisibility';

// Interface do template
interface WhatsAppTemplate {
  id: string;
  inbox_id: string;
  template_id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  rejection_reason: string | null;
  components: any;
  variables: string[] | null;
  example_values: Record<string, string> | null;
  provider_metadata: any;
  created_at: string;
  submitted_at: string | null;
  approved_at: string | null;
  classification_id: string | null;
  inbox?: {
    name: string;
    identifier: string;
  };
  classification?: {
    id: string;
    name: string;
    color: string;
  } | null;
  visibility?: EntityVisibility | null;
}

// Configuração de status
interface StatusConfig {
  label: string;
  icon: React.ElementType;
  color: string;
}

// Configuração de categoria
interface CategoryConfig {
  label: string;
  color: string;
}

interface TemplateCardProps {
  template: WhatsAppTemplate;
  providerName?: string; // Nome do provider para exibição
  statusConfig: Record<string, StatusConfig>;
  categoryConfig: Record<string, CategoryConfig>;
  onEdit: (template: WhatsAppTemplate) => void;
  onPreview: (template: WhatsAppTemplate) => void;
  onDelete: (template: WhatsAppTemplate) => void;
  /** Abre o diálogo de visibilidade. */
  onManageVisibility?: (template: WhatsAppTemplate) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

/**
 * Extrai texto do body do template para preview
 */
const getBodyPreview = (components: any): string => {
  if (!components || !Array.isArray(components)) return '';
  
  const bodyComponent = components.find((c: any) => c.type === 'BODY');
  if (!bodyComponent?.text) return '';
  
  const text = bodyComponent.text;
  // Limite aumentado para aproveitar espaço horizontal
  return text.length > 110 ? `${text.substring(0, 110)}...` : text;
};

/**
 * Conta variáveis no template
 */
const countVariables = (components: any): number => {
  if (!components || !Array.isArray(components)) return 0;
  
  let count = 0;
  components.forEach((c: any) => {
    if (c.text) {
      const matches = c.text.match(/\{\{\d+\}\}/g);
      if (matches) count += matches.length;
    }
  });
  return count;
};

/**
 * Formata data para exibição
 */
const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export function TemplateCard({
  template,
  providerName,
  statusConfig,
  categoryConfig,
  onEdit,
  onPreview,
  onDelete,
  onManageVisibility,
  canEdit = true,
  canDelete = true,
}: TemplateCardProps) {
  const status = statusConfig[template.status] || statusConfig.PENDING;
  const category = categoryConfig[template.category] || categoryConfig.UTILITY;
  const StatusIcon = status.icon;
  const bodyPreview = getBodyPreview(template.components);
  const variableCount = countVariables(template.components);

  return (
    <Card className="group relative overflow-hidden transition-all duration-200 hover:shadow-md">
      <CardHeader className="pb-2 pt-3 px-3 relative">
        {/* Container com gap fixo entre título e botão de ações */}
        <div className="flex items-start justify-between gap-2">
          {/* Área do título - min-w-0 permite truncamento, flex-1 ocupa espaço disponível */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="p-1.5 rounded-lg bg-primary/10 shrink-0">
              <FileText className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-medium text-sm text-foreground truncate max-w-full" title={template.name}>
                {template.name}
              </h3>
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <Badge variant="outline" className={cn("text-[10px] py-0 h-4 shrink-0", category.color)}>
                  {category.label}
                </Badge>
                {/* Badge de classificação gerenciável */}
                {template.classification && (
                  <Badge 
                    variant="outline" 
                    className="text-[10px] py-0 h-4 shrink-0"
                    style={{ 
                      borderColor: template.classification.color,
                      color: template.classification.color 
                    }}
                  >
                    {template.classification.name}
                  </Badge>
                )}
                <div className="flex items-center gap-0.5 text-[10px] text-muted-foreground shrink-0">
                  <Globe className="h-2.5 w-2.5" />
                  {template.language}
                </div>
                {/* Badge do Provider */}
                {providerName && (
                  <Badge variant="secondary" className="text-[10px] py-0 h-4 shrink-0">
                    {providerName}
                  </Badge>
                )}
                {/* Badge de visibilidade */}
                <VisibilityBadge
                  visibility={template.visibility ?? 'public'}
                  className="text-[10px] py-0 h-4 shrink-0"
                />

              </div>
            </div>
          </div>

          {/* Botão de ações - shrink-0 garante que nunca desaparece */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onPreview(template)}>
                <Eye className="h-4 w-4 mr-2" />
                Visualizar
              </DropdownMenuItem>
              {canEdit && (
                <DropdownMenuItem onClick={() => onEdit(template)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </DropdownMenuItem>
              )}
              {onManageVisibility && (
                <DropdownMenuItem onClick={() => onManageVisibility(template)}>
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  Visibilidade
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem 
                  onClick={() => onDelete(template)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 pb-2 px-3 space-y-1.5 relative">
        {/* Preview do corpo */}
        {bodyPreview && (
          <div className="p-2 rounded-md bg-muted/50 text-xs text-muted-foreground leading-tight">
            <MessageSquare className="h-3 w-3 inline mr-1.5 opacity-50" />
            {bodyPreview}
          </div>
        )}

        {/* Footer com variáveis e status */}
        <div className="flex items-center justify-between pt-1">
          {/* Variáveis */}
          {variableCount > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono text-muted-foreground">
              {variableCount} var{variableCount > 1 ? 's' : ''}
            </span>
          )}
          {variableCount === 0 && <span />}

          {/* Status */}
          <Badge variant="outline" className={cn("text-[10px] py-0 h-4", status.color)}>
            <StatusIcon className="h-2.5 w-2.5 mr-0.5" />
            {status.label}
          </Badge>
        </div>

        {/* Motivo da rejeição - exibido apenas para templates rejeitados */}
        {template.status === 'REJECTED' && template.rejection_reason && (
          <div className="p-2 rounded-md bg-destructive/10 text-destructive text-[11px] leading-tight">
            <strong>Motivo:</strong> {template.rejection_reason}
          </div>
        )}

        {/* Data de criação */}
        <div className="text-[10px] text-muted-foreground text-right">
          {formatDate(template.created_at)}
        </div>
      </CardContent>
    </Card>
  );
}
